/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo, useState } from 'react';
import ReactSelect, { OptionProps, components } from 'react-select';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Icon from 'src/components/ui/Icon/Icon';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { OrderTag, useSetOrderTags, useTagCreate, useTagDelete, useTagsQuery } from 'src/services/tags';
import { TagOption, buildCreateOption, getTagChipColors, hasExactMatch, mapTagsToOptions } from './helpers/tagHelpers';
import styles from './OrderTagEditor.module.scss';

export interface OrderTagEditorProps {
  saleOrderId: number;
  tags: OrderTag[];
}

const OrderTagEditor: React.FC<OrderTagEditorProps> = ({ saleOrderId, tags }) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [tagToDelete, setTagToDelete] = useState<OrderTag | null>(null);

  const { data: searchedTags, isFetching } = useTagsQuery(search);
  const createTagMutation = useTagCreate();
  const deleteTagMutation = useTagDelete();
  const setOrderTagsMutation = useSetOrderTags();

  const assignedIds = useMemo(() => tags.map(tag => tag.id), [tags]);

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 300), []);

  const options = useMemo(() => {
    const available = mapTagsToOptions(searchedTags ?? [], assignedIds);
    if (inputValue.trim() && !hasExactMatch(searchedTags ?? [], inputValue)) {
      return [...available, buildCreateOption(inputValue)];
    }
    return available;
  }, [searchedTags, assignedIds, inputValue]);

  const resetInput = () => {
    setInputValue('');
    setSearch('');
  };

  const putTagIds = async (tagIds: number[]) => {
    try {
      await setOrderTagsMutation.mutateAsync({
        params: { path: { sale_order_id: saleOrderId } },
        body: { tag_ids: tagIds },
      });
    } catch (error) {
      toast.error(t('errors.update_tags_failed'));
    }
  };

  const handleRemove = (tagId: number) => {
    if (setOrderTagsMutation.isPending) {
      return;
    }
    putTagIds(assignedIds.filter(id => id !== tagId));
  };

  const handleSelect = async (option: TagOption | null) => {
    if (createTagMutation.isPending || setOrderTagsMutation.isPending) {
      return;
    }
    if (!option) {
      return;
    }
    try {
      let tagId: number;
      if (option.isCreate) {
        const created = await createTagMutation.mutateAsync({ body: { name: option.label } });
        tagId = created.id;
      } else {
        tagId = parseInt(option.value, 10);
      }
      if (!Number.isNaN(tagId) && !assignedIds.includes(tagId)) {
        await putTagIds([...assignedIds, tagId]);
      }
    } catch (error) {
      toast.error(t('errors.update_tags_failed'));
    } finally {
      resetInput();
    }
  };

  const TagOptionComponent = (props: OptionProps<TagOption, false>) => {
    const { data } = props;
    const chip = getTagChipColors(data.label);
    return (
      <components.Option {...props}>
        <div className="flex items-center justify-between w-full gap-x-8">
          <div className="flex items-center gap-x-8">
            {!data.isCreate && (
              <span
                className="inline-block"
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  backgroundColor: chip.backgroundColor,
                  border: `1px solid ${chip.color}`,
                  flexShrink: 0,
                }}
              />
            )}
            <span className={data.isCreate ? 'typo-text-14--bold' : 'typo-text-14'}>
              {data.isCreate ? t('outbound.create_tag', { name: data.label }) : data.label}
            </span>
          </div>
          {!data.isCreate && !data.isGlobal && (
            <button
              type="button"
              aria-label={t('outbound.delete_tag_title')}
              onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
                const match = (searchedTags ?? []).find(tag => String(tag.id) === data.value);
                if (match) {
                  setTagToDelete(match);
                }
              }}
              onClick={event => event.stopPropagation()}>
              <Icon name="delete" width={14} height={14} className="text-faded" />
            </button>
          )}
        </div>
      </components.Option>
    );
  };

  return (
    <div className="flex flex-col flex-1 gap-y-8">
      <div className={`${styles.root} flex flex-wrap items-center`} style={{ gap: 5, minHeight: 26 }}>
        {tags.map(tag => (
          <span
            key={tag.id}
            className="w-max typo-text-12--bold"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              ...getTagChipColors(tag.name),
              borderRadius: 12,
              padding: '4px 10px',
              whiteSpace: 'nowrap',
            }}>
            {tag.name}
            <button
              type="button"
              aria-label={t('outbound.remove_tag')}
              title={t('outbound.remove_tag')}
              disabled={setOrderTagsMutation.isPending}
              onClick={() => handleRemove(tag.id)}
              style={{
                cursor: 'pointer',
                opacity: 0.55,
                fontSize: 14,
                lineHeight: 1,
                marginRight: -1,
                color: 'inherit',
                background: 'none',
                border: 0,
                padding: 0,
              }}>
              ×
            </button>
          </span>
        ))}
        <div className="flex-1" style={{ minWidth: 110 }}>
          <ReactSelect<TagOption>
            menuIsOpen={isFocused}
            classNamePrefix="tagselect"
            options={options}
            value={null}
            filterOption={() => true}
            isLoading={isFetching}
            placeholder={t('outbound.add_tag')}
            noOptionsMessage={() => null}
            onInputChange={value => {
              setInputValue(value);
              debouncedSetSearch(value);
            }}
            onChange={option => handleSelect(option as TagOption | null)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              resetInput();
            }}
            styles={{
              control: base => ({
                ...base,
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                minHeight: 26,
                cursor: 'text',
              }),
              valueContainer: base => ({ ...base, padding: 0 }),
              input: base => ({
                ...base,
                margin: 0,
                padding: '3px 2px',
                fontFamily: 'Manrope, system-ui, sans-serif',
                fontSize: 14,
                color: 'var(--text-full)',
              }),
              placeholder: base => ({
                ...base,
                color: 'var(--text-disabled)',
                fontFamily: 'Manrope, system-ui, sans-serif',
                fontSize: 14,
              }),
              menu: base => ({
                ...base,
                zIndex: 9999,
                marginTop: 4,
                minWidth: 210,
                backgroundColor: 'var(--surface-elevated)',
                border: '1px solid var(--border-distinct)',
                borderRadius: 4,
                boxShadow: 'none',
              }),
              menuList: base => ({ ...base, paddingTop: 4, paddingBottom: 4, maxHeight: 210 }),
              option: (base, state) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                color: state.data.isCreate ? 'var(--primary)' : 'var(--text-full)',
                backgroundColor: state.isFocused ? 'var(--surface-elevated-more)' : 'var(--surface-elevated)',
                cursor: 'pointer',
                borderTop: state.data.isCreate ? '1px solid var(--border-subtle)' : undefined,
              }),
            }}
            components={{ Option: TagOptionComponent, DropdownIndicator: null, IndicatorSeparator: null }}
          />
        </div>
      </div>
      {tagToDelete && (
        <ConfirmationModal
          isOpen
          title={t('outbound.delete_tag_title')}
          description={<>{t('outbound.delete_tag_text', { name: tagToDelete.name })}</>}
          onClose={async confirmed => {
            if (confirmed) {
              try {
                await deleteTagMutation.mutateAsync({ params: { path: { tag_id: tagToDelete.id } } });
              } catch (error) {
                toast.error(t('errors.delete_tag_failed'));
              }
            }
            setTagToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default OrderTagEditor;
